import { authFetch, buildWorkspaceUrl, throwApiError } from './core'
import type { MemberRole } from './models'

export interface BackendUserInfo {
  sub: string
  name?: string
  email?: string
  picture?: string
  email_verified?: boolean
  fullname?: string
  firstName?: string
  lastName?: string
  avatar?: string
  logoFileId?: string
  roles?: string[]
  enabled?: boolean
  [key: string]: unknown
}

export interface WorkspaceInfo {
  id: string
  name: string
  domain: string
  description?: string | null
  status?: string
  joined?: boolean
  roleCode?: string | null
  lastAccessedAt?: string | null
  [key: string]: unknown
}

export type WorkspaceSummary = WorkspaceInfo

export interface MemberInfo {
  id: string
  userId: string
  name?: string
  username?: string
  type: string
  role: MemberRole
  [key: string]: unknown
}

export interface WorkspaceSessionUser {
  workspaceId: string
  userId: string | null
  name: string | null
  username: string | null
  role: MemberRole | null
}

export interface WorkspaceSessionSnapshot extends WorkspaceSessionUser {
  workspaces: WorkspaceSummary[]
  activeWorkspace: WorkspaceSummary | null
  memberType: string | null
  workspaceEntered: boolean
}

export interface CreateWorkspacePayload {
  name: string
  description?: string
  invitationCode?: string
  isDomainDiscoverable?: boolean
}

const SELECTED_WORKSPACE_STORAGE_KEY = 'robolabx.chatbot.selectedWorkspaceId'

let cachedWorkspaceId: string | null = null
let cachedWorkspaces: WorkspaceSummary[] = []
let cachedActiveWorkspace: WorkspaceSummary | null = null
let cachedWorkspaceEntered = false
let cachedMemberType: string | null = null
let cachedMemberRole: MemberRole | null = null
let cachedMemberName: string | null = null
let cachedMemberUsername: string | null = null
let cachedMemberUserId: string | null = null
let initPromise: Promise<WorkspaceSessionSnapshot> | null = null

function resolveExtraHeaders(
  tokenOrExtra?: string | Record<string, string>,
  maybeExtra?: Record<string, string>,
): Record<string, string> {
  if (typeof tokenOrExtra === 'string') {
    if (tokenOrExtra === 'cookie-session') {
      return maybeExtra ?? {}
    }

    return {
      ...(maybeExtra ?? {}),
      Authorization: `Bearer ${tokenOrExtra}`,
    }
  }

  if (tokenOrExtra == null) {
    return maybeExtra ?? {}
  }

  return tokenOrExtra
}

export function getWorkspaceId(): string | null {
  return cachedWorkspaceId
}

export function getWorkspaces(): WorkspaceSummary[] {
  return cachedWorkspaces
}

export function getActiveWorkspace(): WorkspaceSummary | null {
  return cachedActiveWorkspace
}

export function getMemberType(): string | null {
  return cachedMemberType
}

export function getMemberRole(): MemberRole | null {
  return cachedMemberRole
}

export function getMemberName(): string | null {
  return cachedMemberName
}

export function getMemberUsername(): string | null {
  return cachedMemberUsername
}

export function getMemberUserId(): string | null {
  return cachedMemberUserId
}

export function requestOptions(
  tokenOrExtra?: string | Record<string, string>,
  extra?: Record<string, string>,
): RequestInit {
  const resolvedExtra = resolveExtraHeaders(tokenOrExtra, extra)
  const headers: Record<string, string> = {
    ...resolvedExtra,
  }

  if (cachedWorkspaceId) {
    headers['x-workspace-id'] = cachedWorkspaceId
  }

  if (cachedMemberRole) {
    headers['x-user-workspace-roles'] = cachedMemberRole
  }

  return {
    credentials: 'include',
    headers,
  }
}

export async function fetchBackendUserInfo(_token?: string): Promise<BackendUserInfo> {
  const response = await authFetch(buildWorkspaceUrl('/api/users/me'))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch userinfo')
  }
  return response.json() as Promise<BackendUserInfo>
}

function readStoredWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage?.getItem(SELECTED_WORKSPACE_STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

function writeStoredWorkspaceId(workspaceId: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage?.setItem(SELECTED_WORKSPACE_STORAGE_KEY, workspaceId)
  } catch {
    // ignore storage failures
  }
}

function toAccessibleWorkspaces(workspaces: WorkspaceSummary[]): WorkspaceSummary[] {
  const joined = workspaces.filter((workspace) => workspace.joined !== false)
  return joined.length > 0 ? joined : workspaces
}

function resolveWorkspace(
  workspaces: WorkspaceSummary[],
  preferredWorkspaceId?: string,
): WorkspaceSummary | null {
  const accessible = toAccessibleWorkspaces(workspaces)
  if (accessible.length === 0) return null

  if (preferredWorkspaceId) {
    const preferred = accessible.find((workspace) => workspace.id === preferredWorkspaceId)
    if (preferred) return preferred
  }

  const storedWorkspaceId = readStoredWorkspaceId()
  if (storedWorkspaceId) {
    const stored = accessible.find((workspace) => workspace.id === storedWorkspaceId)
    if (stored) return stored
  }

  return accessible[0]
}

function snapshot(): WorkspaceSessionSnapshot {
  return {
    workspaceId: cachedWorkspaceId ?? '',
    workspaces: cachedWorkspaces,
    activeWorkspace: cachedActiveWorkspace,
    memberType: cachedMemberType,
    workspaceEntered: cachedWorkspaceEntered,
    userId: cachedMemberUserId,
    name: cachedMemberName,
    username: cachedMemberUsername,
    role: cachedMemberRole,
  }
}

function cacheMember(member: MemberInfo): void {
  cachedMemberType = member.type
  cachedMemberRole = member.role ?? null
  cachedMemberName = member.name ?? null
  cachedMemberUsername = member.username ?? null
  cachedMemberUserId = member.userId ?? null
}

export function getWorkspaceSessionSnapshot(): WorkspaceSessionSnapshot {
  return snapshot()
}

export async function fetchAvailableWorkspaces(_token?: string): Promise<WorkspaceSummary[]> {
  const response = await authFetch(
    buildWorkspaceUrl('/api/workspace/users/workspaces'),
    requestOptions(_token),
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch workspaces')
  }
  return response.json() as Promise<WorkspaceSummary[]>
}

async function fetchMemberInfo(_token?: string): Promise<MemberInfo> {
  const response = await authFetch(
    buildWorkspaceUrl('/api/workspace/users/workspaces/me/member'),
    requestOptions(_token),
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch member info')
  }
  return response.json() as Promise<MemberInfo>
}

export async function refreshMemberInfo(token?: string): Promise<MemberInfo> {
  const member = await fetchMemberInfo(token)
  cacheMember(member)
  return member
}

export async function enterWorkspace(workspaceId: string, token?: string): Promise<void> {
  const response = await authFetch(
    buildWorkspaceUrl(`/api/workspace/users/workspaces/${encodeURIComponent(workspaceId)}/actions/enter`),
    {
      method: 'PUT',
      ...requestOptions(token),
    },
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to enter workspace')
  }
}

export function clearWorkspaceCache(): void {
  cachedWorkspaceId = null
  cachedWorkspaces = []
  cachedActiveWorkspace = null
  cachedWorkspaceEntered = false
  cachedMemberType = null
  cachedMemberRole = null
  cachedMemberName = null
  cachedMemberUsername = null
  cachedMemberUserId = null
  initPromise = null
}

export async function selectWorkspace(workspaceId: string, token?: string): Promise<WorkspaceSessionSnapshot> {
  const workspace = cachedWorkspaces.find((item) => item.id === workspaceId)
  if (!workspace) {
    throw new Error('Selected workspace is not available for this user')
  }

  await enterWorkspace(workspace.id, token)

  cachedWorkspaceId = workspace.id
  cachedActiveWorkspace = workspace
  cachedWorkspaceEntered = true
  writeStoredWorkspaceId(workspace.id)

  await refreshMemberInfo(token)
  return snapshot()
}

async function loadWorkspaceSession(
  token?: string,
  preferredWorkspaceId?: string,
): Promise<WorkspaceSessionSnapshot> {
  const workspaces = await fetchAvailableWorkspaces(token)
  if (workspaces.length === 0) {
    throw new Error('No workspaces available for this user')
  }

  cachedWorkspaces = workspaces
  const workspace = resolveWorkspace(workspaces, preferredWorkspaceId)
  if (!workspace) {
    throw new Error('No workspaces available for this user')
  }

  await enterWorkspace(workspace.id, token)

  cachedWorkspaceId = workspace.id
  cachedActiveWorkspace = workspace
  cachedWorkspaceEntered = true
  writeStoredWorkspaceId(workspace.id)

  await refreshMemberInfo(token)
  return snapshot()
}

export function initWorkspace(
  tokenOrPreferred?: string,
  maybePreferred?: string,
): Promise<WorkspaceSessionSnapshot> {
  const token = tokenOrPreferred
  const preferredWorkspaceId = maybePreferred ?? undefined
  if (cachedWorkspaceId !== null) return Promise.resolve(snapshot())
  if (initPromise) return initPromise

  initPromise = loadWorkspaceSession(token, preferredWorkspaceId).finally(() => {
    initPromise = null
  })

  return initPromise
}

export function reloadWorkspaceSession(token?: string): Promise<WorkspaceSessionSnapshot> {
  initPromise = null
  return loadWorkspaceSession(token)
}

export async function createWorkspace(payload: CreateWorkspacePayload): Promise<WorkspaceSessionSnapshot> {
  const response = await authFetch(
    buildWorkspaceUrl('/api/workspace/users/workspaces/robolabx'),
    {
      method: 'POST',
      ...requestOptions({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        name: payload.name.trim(),
        description: payload.description?.trim() || undefined,
        invitationCode: payload.invitationCode?.trim() || undefined,
        isDomainDiscoverable: payload.isDomainDiscoverable ?? false,
      }),
    },
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to create workspace')
  }

  const created = await response.json() as WorkspaceSummary
  cachedWorkspaces = await fetchAvailableWorkspaces()
  const existingWorkspace = cachedWorkspaces.find((workspace) => workspace.id === created.id)
  if (!existingWorkspace) {
    cachedWorkspaces = [created, ...cachedWorkspaces]
  }
  return selectWorkspace((existingWorkspace ?? created).id)
}

export async function bootstrapWorkspaceSession(token?: string): Promise<WorkspaceSessionUser> {
  await initWorkspace(token)

  if (!cachedWorkspaceId) {
    throw new Error('Workspace session bootstrap completed without a workspace id')
  }

  return {
    workspaceId: cachedWorkspaceId,
    userId: cachedMemberUserId,
    name: cachedMemberName,
    username: cachedMemberUsername,
    role: cachedMemberRole,
  }
}
